import React, { useState, useRef, useEffect } from 'react';
import { Bell, Check, CheckCheck, Wifi, WifiOff } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';

const typeColors = {
  new_lead: 'bg-blue-500',
  new_offer: 'bg-green-500',
  offer_accepted: 'bg-emerald-500',
  vehicle_sold: 'bg-amber-500',
  subscription_updated: 'bg-purple-500',
  system_alert: 'bg-red-500',
  info: 'bg-gray-500',
  success: 'bg-green-500',
  warning: 'bg-amber-500',
  error: 'bg-red-500',
};

const typeLabels = {
  new_lead: 'New Lead',
  new_offer: 'Offer',
  offer_accepted: 'Accepted',
  vehicle_sold: 'Sold',
  subscription_updated: 'Subscription',
  system_alert: 'System',
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function NotificationBell() {
  const { notifications, unreadCount, connected, markAsRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell">
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-xl hover:bg-gray-100 transition-colors"
        data-testid="notification-bell-btn"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 z-[60] overflow-hidden" data-testid="notification-panel">
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-gray-900">Notifications</h3>
              <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-gray-300'}`} title={connected ? 'Live' : 'Disconnected'} />
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`px-5 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer flex gap-3 ${!notif.read ? 'bg-blue-50/50' : ''}`}
                  onClick={() => { if (!notif.read) markAsRead(notif.id); }}
                  data-testid={`notification-item-${notif.id}`}
                >
                  <div className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${typeColors[notif.type] || 'bg-gray-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-sm font-medium truncate ${!notif.read ? 'text-gray-900' : 'text-gray-600'}`}>
                        {notif.title}
                      </p>
                      <span className="text-xs text-gray-400 shrink-0">{timeAgo(notif.created_at)}</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">{notif.message}</p>
                    {notif.type && typeLabels[notif.type] && (
                      <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full text-white ${typeColors[notif.type] || 'bg-gray-400'}`}>
                        {typeLabels[notif.type]}
                      </span>
                    )}
                  </div>
                  {!notif.read && (
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 shrink-0" />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-400 flex items-center gap-1">
              {connected ? <Wifi className="w-3 h-3 text-green-500" /> : <WifiOff className="w-3 h-3 text-gray-400" />}
              {connected ? 'Live updates' : 'Reconnecting...'}
            </span>
            <span className="text-xs text-gray-400">{notifications.length} notifications</span>
          </div>
        </div>
      )}
    </div>
  );
}
